import { Component, computed, Inject, model, signal } from '@angular/core';
import { ImportService } from '../../../services/import-service';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AutoSizeStrategy, ColDef, GridApi, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from "@angular/material/icon";
import { EditableColumnHeaderComponent } from './editable-column-header-component/editable-column-header-component';
import { EligibilityFile } from '../../../models/eligibility-file';
import { SessionService } from '../../../services/session-service';
import { FormsModule } from '@angular/forms';
import { LoadingOverlayComponent } from '../../util/loading-overlay-component/loading-overlay-component';
import { ConfirmationDialogComponent } from '../../util/confirmation-dialog-component/confirmation-dialog-component';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { SnackBarService } from '../../../services/snack-bar-service';

@Component({
  selector: 'app-import-sites-from-csv-modal-component',
  imports: [MatDialogModule, AgGridAngular, MatInputModule, MatButtonModule, MatIconModule, MatCheckboxModule, FormsModule],
  templateUrl: './import-sites-from-csv-modal-component.html',
  styleUrl: './import-sites-from-csv-modal-component.css',
})
export class ImportSitesFromCsvModalComponent {
  private siteSelectionGridApi!: GridApi;

  public readonly gridContext = {
    componentParent: this
  };

  public readonly gridLocale = AG_GRID_LOCALE_FR;

  public readonly autoSizeStrategy: AutoSizeStrategy = { type: 'fitGridWidth' };
  public readonly loadingOverlayComponent: any = LoadingOverlayComponent;
  public readonly loadingOverlayComponentParams: any = { loadingMessage: 'Chargement des résultats...' };

  public readonly siteSelectionColumns: ColDef[] = [];
  public readonly formattedRows = signal<any[]>([]);
  public readonly siteSelectionRowData = computed<any[]>(() => {
    const formattedRows = this.formattedRows();

    return this.includeHeader() ? formattedRows.slice(1) : formattedRows;
  });

  public readonly sessionFiles = signal<EligibilityFile[]>([]);
  public readonly selectedFile = signal<any>(null);
  public readonly selectedFileName = model<string>('Aucun fichier sélectionné');
  public readonly selectedRows = signal<number[]>([]);
  public readonly includeHeader = model<boolean>(true);

  private readonly columnCount = signal<number>(0);
  private readonly FIT_GRID_WIDTH_MAX_COLUMN_COUNT = 5;
  public columnFields!: (string | undefined | null)[]; // Maps the column fields into a list like ['name', null, 'address_line_1', ...]
  public fileUuid!: string | undefined;

  public isColumnFieldSet!: boolean;
  public isImportingSites = signal<boolean>(false);
  
  constructor(
    private readonly importService: ImportService,
    private readonly sessionService: SessionService,
    private readonly dialogRef: MatDialogRef<ImportSitesFromCsvModalComponent>,
    private readonly snackBar: SnackBarService,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) private readonly data: any
  ) {
    this.isColumnFieldSet = false;
  }

  onSiteSelectionGridReady(params: GridReadyEvent): void {
    this.siteSelectionGridApi = params.api;
  }

  onSiteSelectionChanged(event: SelectionChangedEvent): void {
    this.selectedRows.set(event.api.getSelectedRows().map((row: any) => row.rowImmutableId));
  }

  // Not used for v1
  getSessionFiles(): void {
    const sessionCode = this.data.sessionCode;
    if (!sessionCode) {
      this.snackBar.error("Erreur lors du chargement des fichiers: Code de session invalide");
      return;
    }

    this.sessionService.getSessionFiles(sessionCode).subscribe({
      next: (response: EligibilityFile[]) => this.sessionFiles.set(response),
      error: () => this.snackBar.error("Erreur lors du chargement des fichiers: Erreur serveur")
    });
  }

  onFileSelected(event: any): void {
    const selectedFile = event.srcElement.files[0];

    if (!selectedFile) return;

    this.selectedFile.set(selectedFile);
    this.selectedFileName.set(selectedFile.name);

    const sessionCode = this.data.sessionCode;
    if (!sessionCode) {
      this.snackBar.error("Erreur lors de l'import du fichier: Code de session invalide");
      return;
    }

    this.siteSelectionGridApi.setGridOption('loading', true);
    this.importService.importCSVFile(sessionCode, this.selectedFile()).subscribe({
      next: (response: EligibilityFile) => {
        this.handleImportResponse(response);
        this.siteSelectionGridApi.setGridOption('loading', false);
      },
      error: () => {
        this.snackBar.error("Erreur lors de l'import du fichier: Erreur serveur");
        this.siteSelectionGridApi.setGridOption('loading', false);
      }
    });
  }

  handleImportResponse(response: EligibilityFile): void {
    this.isColumnFieldSet = false;
    this.fileUuid = response.uuid;

    const firstRow = response.rows[0];
    this.initializeColumnFields(firstRow.length);

    let rowsToHandle = response.rows;

    const columnDefs = this.generateColumnDef(firstRow, response.columns);
    const formattedRows = this.formatRows(rowsToHandle); 

    this.columnCount.set(columnDefs.length);
    this.siteSelectionGridApi.setGridOption("columnDefs", columnDefs);
    this.formattedRows.set(formattedRows);
  }

  initializeColumnFields(columnCount: number): void {
    this.columnFields = [];
    for (var column = 0; column < columnCount; column++) {
      this.columnFields[column] = null;
    }
  }

  onRowDataUpdated() {
    if (!this.siteSelectionGridApi || this.siteSelectionGridApi.isDestroyed()) {
      return;
    }

    if (this.columnCount() < this.FIT_GRID_WIDTH_MAX_COLUMN_COUNT) {
      this.siteSelectionGridApi.sizeColumnsToFit();
    } else {
      this.siteSelectionGridApi.autoSizeAllColumns();
    }
    this.siteSelectionGridApi.selectAll();
  }

  formatRows(rows: string[][]) {
    const formattedRows: any[] = [];

    let rowImmutableId = 0;
    for (const row of rows) {
      const formattedRow: Record<string, any> = {};

      let columnId = 0;
      for (const field of row) {
        formattedRow[`${columnId}`] = field;
        columnId++;
      }

      formattedRow['rowImmutableId'] = rowImmutableId; // ID that doesn't change when filters or sorts are applied
      formattedRows.push(formattedRow);
      rowImmutableId++;
    }
    
    return formattedRows;
  }

  generateColumnDef(headerRow: string[], columns: (string | null)[]) {
    const columnDefs: ColDef[] = [];

    for (const [columnId, field] of headerRow.entries()) {
      const headerComponentParams = { presetField: columns[columnId], columnId: columnId };
      if (columns[columnId] !== null) {
        this.assignColumnToField(`${columnId}`, columns[columnId]);
      }
      columnDefs.push(
        {
          headerName: '',
          field: `${columnId}`,
          filter: "agTextColumnFilter", 
          floatingFilter: true,
          suppressMovable: true,
          headerComponent: EditableColumnHeaderComponent,
          headerComponentParams: headerComponentParams,
          headerClass: "w-full"
        }
      );
    }

    return columnDefs;
  }

  assignColumnToField(columnIdString: string, field: string): void {
    const columnId: number = +columnIdString;
    this.columnFields[columnId] = field;
    this.isColumnFieldSet = true;
  }

  checkColumnFieldsThenImportSites(): void {
    if (!this.columnFields.find((columnField: string | undefined | null) => columnField !== undefined && columnField !== null)) {
      this.snackBar.warning("Aucun champ n'a été assigné aux colonnes !");
      this.isImportingSites.set(false);
      return;
    }

    if (this.columnFields.includes(null)) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {content: "Toutes les colonnes n'ont pas de champ assigné, êtes-vous sûr de vouloir continuer ?"},
      });

      dialogRef.afterClosed().subscribe((closeValue: boolean) => {
        if (closeValue) {
          this.isImportingSites.set(true);
          this.importSites();
        }
      });
    } else {
      this.isImportingSites.set(true);
      this.importSites();
    }
  }

  importSites(): void {
    if (this.selectedRows().length === 0) {
      this.snackBar.warning("Veuillez sélectionner des sites à importer");
      return;
    }

    const sessionCode = this.data.sessionCode;
    if (!sessionCode) {
      this.snackBar.error("Code de session invalide");
      return;
    }

    if (!this.fileUuid) {
      this.snackBar.error("Fichier introuvable");
      return;
    }

    this.importService.importSitesFromCSVFile(sessionCode, this.fileUuid, this.selectedRows(), this.columnFields).subscribe({
      next: (sites) => { 
        this.snackBar.success("Sites importés avec succès !");
        this.dialogRef.close(sites);
        this.isImportingSites.set(false);
      },
      error: () => {
        this.snackBar.error("Erreur lors de l'import des sites: Erreur serveur");
        this.isImportingSites.set(false);
      }
    });
  }

  close(closeValue: boolean): void {
    this.dialogRef.close(closeValue);
  }

  removeFile(): void {
    this.selectedFile.set(undefined);
    this.fileUuid = undefined;
    this.siteSelectionGridApi.setGridOption("columnDefs", []);
    this.formattedRows.set([]);
  }
}
